import React, { useEffect,useRef, useState } from 'react';

import ClearIcon from '@mui/icons-material/Clear';
import { IconButton } from '@mui/material';
import { FormHelperText, Link, Stack, TextField, Tooltip } from '@mui/material';

interface AcFileInputProps {
  header?: string;
  tooltip?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  customClass?: string;
  dataTestId?: string;
  accept?: string;
  maxSize?: number;
  onFileSelect: (file: File | null) => void;
  error?: boolean;
  value?: File | null;
}

const AcFileInput = React.forwardRef(
  (
    {
      header = '',
      tooltip = '',
      helperText = '',
      required = false,
      disabled = false,
      customClass = '',
      dataTestId = '',
      accept = '*',
      maxSize = 5,
      onFileSelect,
      error = false,
      value = null
    }: AcFileInputProps,
    ref
  ) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      setSelectedFile(value);
    }, [value]);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setSelectedFile(file);
      onFileSelect(file);
    };

    const handleRemoveFile = () => {
      setSelectedFile(null);
      onFileSelect(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    return (
      <Stack
        className={`input-container formContent-input ${customClass}`}
        direction="column"
      >
        <Stack
          direction="row"
          alignItems="center"
          marginBottom={'4px'}
          gap={'5px'}
          className="input-headerBlock formContent-input-fieldTitle"
        >
          {header && (
            <Tooltip arrow title={tooltip} placement="top">
              <h3>
                {required && (
                  <span style={{ color: 'red', marginRight: '6px' }}>*</span>
                )}
                {header}
              </h3>
            </Tooltip>
          )}
        </Stack>
        <TextField
          variant="outlined"
          fullWidth
          value={selectedFile?.name || ''}
          placeholder="Upload File"
          InputProps={{
            readOnly: true,
            sx: {
              height: '48px',
              '& .MuiOutlinedInput-input': {
                padding: '12px 14px',
                height: '24px',
                lineHeight: '24px'
              }
            },
            endAdornment: selectedFile && !disabled && (
              <IconButton
                aria-label="Remove file"
                onClick={handleRemoveFile}
                edge="end"
                size="small"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )
          }}
          error={error}
          disabled={disabled}
        />

        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileSelect}
          accept={accept}
          data-testid={dataTestId}
        />

        <Link
          component="button"
          variant="body2"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          sx={{
            alignSelf: 'flex-start',
            textDecoration: 'none',
            marginTop: '4px',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          Upload
        </Link>

        {helperText && (
          <FormHelperText error={error}>{helperText}</FormHelperText>
        )}
      </Stack>
    );
  }
);

export default AcFileInput;
